import { Card, Col, Row, Statistic, Table, Tag, Typography } from 'antd'
import { MobileOutlined, WifiOutlined, DisconnectOutlined, MessageOutlined } from '@ant-design/icons'
import { useDeviceList } from '../hooks/useDevices'
import { useQuery } from '@tanstack/react-query'
import { interactApi } from '../api/interact'

const { Title } = Typography

export default function Dashboard() {
  const { data: deviceData, isLoading } = useDeviceList({ page_size: 100 })

  const devices = deviceData?.items || []
  const onlineCount = devices.filter((d) => d.status === 'online').length
  const offlineCount = devices.length - onlineCount

  const recentHistoryQuery = useQuery({
    queryKey: ['recent-history'],
    queryFn: async () => {
      if (devices.length === 0) return []
      const results = await Promise.all(
        devices.slice(0, 3).map((d) => interactApi.history(d.id, { page_size: 5 }))
      )
      return results.flatMap((r) => r.items).sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ).slice(0, 10)
    },
    enabled: devices.length > 0,
  })

  const historyColumns = [
    {
      title: '设备',
      dataIndex: 'device_id',
      width: 120,
      render: (id: string) => {
        const d = devices.find((dev) => dev.id === id)
        return d?.name || id.slice(0, 8) + '...'
      },
    },
    {
      title: '输入类型',
      dataIndex: 'input_type',
      width: 80,
      render: (t: string) => (
        <Tag color={t === 'voice' ? 'blue' : 'green'}>{t === 'voice' ? '语音' : '文本'}</Tag>
      ),
    },
    { title: '输入内容', dataIndex: 'text_input', ellipsis: true },
    { title: '回复', dataIndex: 'response_text', ellipsis: true },
    {
      title: '后端',
      dataIndex: 'backend_used',
      width: 90,
      render: (b: string) => b ? (
        <Tag color={b === 'local' ? 'green' : 'blue'}>{b === 'local' ? '本地' : '远程'}</Tag>
      ) : '-',
    },
    {
      title: '时间',
      dataIndex: 'created_at',
      width: 160,
      render: (t: string) => new Date(t).toLocaleString('zh-CN'),
    },
  ]

  return (
    <div>
      <Title level={4} style={{ marginBottom: 20 }}>系统总览</Title>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="设备总数"
              value={devices.length}
              prefix={<MobileOutlined />}
              loading={isLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="在线设备"
              value={onlineCount}
              valueStyle={{ color: '#52c41a' }}
              prefix={<WifiOutlined />}
              loading={isLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="离线设备"
              value={offlineCount}
              valueStyle={{ color: '#aaa' }}
              prefix={<DisconnectOutlined />}
              loading={isLoading}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={<><MessageOutlined /> 最近交互记录</>}
        style={{ marginTop: 16 }}
        loading={recentHistoryQuery.isLoading}
      >
        <Table
          dataSource={recentHistoryQuery.data || []}
          columns={historyColumns}
          rowKey="id"
          size="small"
          pagination={false}
          locale={{ emptyText: '暂无交互记录' }}
        />
      </Card>
    </div>
  )
}
